import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, UserPlus, X, Archive, Trash2, Crown, FolderKanban } from "lucide-react";
import { api, formatApiError, PROJECT_LIFECYCLE_LABELS, PROJECT_LIFECYCLE_STYLE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import RoleBadge from "@/components/RoleBadge";
import { toast } from "sonner";

export default function WorkspaceDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ws, setWs] = useState(null);
  const [members, setMembers] = useState({ members: [], managers: [] });
  const [allUsers, setAllUsers] = useState([]);
  const [projects, setProjects] = useState(null);
  const [error, setError] = useState("");
  const [modal, setModal] = useState(null); // 'member' | 'manager' | null
  const [selected, setSelected] = useState("");

  const isAdmin = user?.role === "admin";
  const isManager = ws?.manager_ids?.includes(user?.id);
  const isInter = ws?.type === "interdepartmental";
  const hasDeptManager = !isInter && (ws?.manager_ids?.length || 0) > 0;
  const canManageMembers = isAdmin; // only admin can add/remove members or managers

  const load = async () => {
    try {
      const [wsRes, mRes, uRes] = await Promise.all([
        api.get(`/workspaces/${id}`),
        api.get(`/workspaces/${id}/members`),
        isAdmin ? api.get("/users") : Promise.resolve({ data: [] }),
      ]);
      setWs(wsRes.data);
      setMembers(mRes.data);
      setAllUsers(uRes.data);
      try {
        const pr = await api.get("/projects", { params: { workspace_id: id } });
        setProjects(pr.data);
      } catch { setProjects([]); }
    } catch (e) { setError(formatApiError(e)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const eligible = allUsers.filter((u) => {
    if (u.status === "suspended") return false;
    const isMember = members.members.some((m) => m.id === u.id);
    const isMgr = members.managers.some((m) => m.id === u.id);
    if (modal === "member") {
      // Adding a regular member: exclude those already in the workspace
      return !isMember && !isMgr;
    }
    if (isInter) {
      // Interdepartamental: somente quem gerencia algum departamento e ainda não participa
      if (isMgr) return false;
      return u.effective_role === "manager" || u.role === "manager";
    }
    // Departamental (definir/substituir gerente): apenas membros do departamento
    // (inclui ex-gerentes que voltaram a ser membros). Não exige remoção prévia.
    return isMember;
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) return;
    try {
      if (modal === "member") {
        await api.post(`/workspaces/${id}/members`, { user_id: selected });
        toast.success("Membro adicionado.");
      } else if (modal === "replace") {
        await api.post(`/workspaces/${id}/managers/replace`, { user_id: selected });
        toast.success("Gerente substituído.");
      } else {
        await api.post(`/workspaces/${id}/managers`, { user_id: selected });
        toast.success(isInter ? "Gerente participante adicionado." : "Gerente definido.");
      }
      setModal(null); setSelected(""); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const removeMember = async (uid) => {
    if (!window.confirm("Remover este membro?")) return;
    try { await api.delete(`/workspaces/${id}/members/${uid}`); toast.success("Membro removido."); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const removeManager = async (uid) => {
    if (!window.confirm("Remover este gerente?")) return;
    try { await api.delete(`/workspaces/${id}/managers/${uid}`); toast.success("Gerente removido."); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const archive = async () => {
    if (!window.confirm("Arquivar este espaço?")) return;
    try { await api.post(`/workspaces/${id}/archive`); toast.success("Espaço arquivado."); navigate("/espacos"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async () => {
    if (!window.confirm("Excluir definitivamente este espaço?")) return;
    try { await api.delete(`/workspaces/${id}`); toast.success("Espaço excluído."); navigate("/espacos"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  if (error) return <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="workspace-error">{error}</div>;
  if (!ws) return <div className="text-slate-500">Carregando...</div>;

  return (
    <div className="space-y-6" data-testid="workspace-detail-page">
      <Link to="/espacos" className="inline-flex items-center text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4 mr-1" /> Voltar
      </Link>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Espaço de trabalho</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-2">{ws.name}</h1>
          <p className="text-slate-500 mt-1 max-w-2xl">{ws.description || "Sem descrição."}</p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <button onClick={archive} className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-slate-200 text-sm hover:bg-slate-50" data-testid="archive-workspace-btn">
              <Archive className="w-4 h-4" /> Arquivar
            </button>
            <button onClick={remove} className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-red-200 text-sm text-red-700 hover:bg-red-50" data-testid="delete-workspace-btn">
              <Trash2 className="w-4 h-4" /> Excluir
            </button>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="managers-section">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold">
            {isInter ? `Gerentes participantes (${members.managers.length})` : `Gerente do departamento${members.managers.length ? "" : " (não definido)"}`}
          </h2>
          {isAdmin && (
            isInter ? (
              <button onClick={() => { setModal("manager"); setSelected(""); }} data-testid="add-participant-btn"
                className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-800">
                <Crown className="w-4 h-4" /> Adicionar gerente participante
              </button>
            ) : hasDeptManager ? (
              <button onClick={() => { setModal("replace"); setSelected(""); }} data-testid="replace-manager-btn"
                className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-800">
                <Crown className="w-4 h-4" /> Substituir gerente
              </button>
            ) : (
              <button onClick={() => { setModal("manager"); setSelected(""); }} data-testid="define-manager-btn"
                className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-800">
                <Crown className="w-4 h-4" /> Definir gerente
              </button>
            )
          )}
        </div>
        <div className="p-6">
          {members.managers.length === 0 ? (
            <p className="text-sm text-slate-500" data-testid="managers-empty">Nenhum gerente definido.</p>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="managers-list">
              {members.managers.map((m) => (
                <li key={m.id} className="flex items-center justify-between py-3" data-testid={`manager-${m.id}`}>
                  <div className="text-sm">
                    <span className="font-medium text-slate-900">{m.name}</span>
                    <span className="text-slate-500 ml-2">{m.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <RoleBadge role={m.local_role || "manager"} />
                    {isAdmin && (
                      <button onClick={() => removeManager(m.id)} className="p-2 rounded-md hover:bg-slate-100" title={isInter ? "Remover do espaço interdepartamental" : "Remover gerente"} data-testid={`remove-manager-${m.id}`}>
                        <X className="w-4 h-4 text-slate-500" />
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {!isInter && (
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="members-section">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold">Membros ({members.members.length})</h2>
          {canManageMembers && (
            <button onClick={() => { setModal("member"); setSelected(""); }} data-testid="add-member-btn"
              className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-800">
              <UserPlus className="w-4 h-4" /> Adicionar membro
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          {members.members.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500" data-testid="members-empty">Nenhum membro neste espaço.</div>
          ) : (
            <table className="w-full text-sm" data-testid="members-table">
              <thead className="bg-slate-50/50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Nome</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">E-mail</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Função neste espaço</th>
                  {canManageMembers && <th className="px-6 py-3" />}
                </tr>
              </thead>
              <tbody>
                {members.members.map((m) => (
                  <tr key={m.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`member-row-${m.id}`}>
                    <td className="px-6 py-3 text-slate-900 font-medium">{m.name}</td>
                    <td className="px-6 py-3 text-slate-600">{m.email}</td>
                    <td className="px-6 py-3"><RoleBadge role={m.local_role || "member"} /></td>
                    {canManageMembers && (
                      <td className="px-6 py-3 text-right">
                        <button onClick={() => removeMember(m.id)} className="p-2 rounded-md hover:bg-slate-100" title="Remover" data-testid={`remove-member-${m.id}`}>
                          <X className="w-4 h-4 text-slate-500" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="ws-projects-section">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="font-heading text-lg font-semibold">Projetos deste espaço{projects ? ` (${projects.length})` : ""}</h2>
          <p className="text-xs text-slate-500 mt-1">{isInter ? "Apenas projetos acessíveis a você são exibidos." : "Projetos acessíveis neste departamento."}</p>
        </div>
        <div className="p-6">
          {projects === null ? (
            <p className="text-sm text-slate-500">Carregando...</p>
          ) : projects.length === 0 ? (
            <div className="text-center py-8" data-testid="ws-projects-empty">
              <FolderKanban className="w-8 h-8 text-slate-300 mx-auto" strokeWidth={1.5} />
              <p className="text-sm text-slate-500 mt-2">Nenhum projeto acessível neste espaço.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ws-projects-grid">
              {projects.map((p) => (
                <Link key={p.id} to={`/projetos/${p.id}`} data-testid={`ws-project-card-${p.id}`}
                  className="block rounded-xl border border-slate-200 p-4 transition-all hover:shadow-md hover:border-slate-300 hover:-translate-y-0.5">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-medium text-slate-900 truncate">{p.name}</h3>
                    <span className={`inline-flex items-center text-xs px-2 py-0.5 rounded-md shrink-0 ${PROJECT_LIFECYCLE_STYLE[p.project_status] || "bg-slate-100 text-slate-600"}`}>{PROJECT_LIFECYCLE_LABELS[p.project_status] || "Em andamento"}</span>
                  </div>
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-slate-500">{p.done_tasks ?? 0} de {p.total_tasks ?? 0} concluídas</span>
                      <span className="text-slate-900 font-semibold">{p.progress ?? 0}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full bg-emerald-500" style={{ width: `${p.progress ?? 0}%` }} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 bg-slate-900/30 grid place-items-center p-4" onClick={() => setModal(null)} data-testid={`assign-${modal}-modal`}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
            className="bg-white rounded-xl border border-slate-200 shadow-lg w-full max-w-md p-6 space-y-4">
            <h2 className="font-heading text-lg font-semibold">
              {modal === "manager" && isInter ? "Adicionar gerente participante"
                : modal === "replace" ? "Substituir gerente do departamento"
                : modal === "manager" ? "Definir gerente do espaço"
                : "Adicionar membro"}
            </h2>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Usuário</label>
              <select required value={selected} onChange={(e) => setSelected(e.target.value)}
                data-testid={`assign-${modal}-select`}
                className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-slate-900">
                <option value="">Selecione...</option>
                {eligible.map((u) => (
                  <option key={u.id} value={u.id}>{u.name} — {u.email}{u.status === "pending" ? " (convite pendente)" : ""}</option>
                ))}
              </select>
              {modal === "manager" && !isInter && (
                <p className="text-xs text-slate-500 mt-2">Espaço departamental aceita apenas um gerente. Para substituir o atual, use "Substituir gerente".</p>
              )}
              {modal === "manager" && isInter && (
                <p className="text-xs text-slate-500 mt-2">Somente usuários que já são gerentes de algum departamento podem participar.</p>
              )}
              {modal === "replace" && (
                <p className="text-xs text-slate-500 mt-2">O gerente atual permanecerá como membro deste espaço.</p>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setModal(null)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Cancelar</button>
              <button type="submit" disabled={!selected} data-testid={`assign-${modal}-submit`}
                className="h-10 px-4 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-50">
                {modal === "manager" && isInter ? "Adicionar"
                  : modal === "replace" ? "Substituir"
                  : modal === "manager" ? "Definir gerente"
                  : "Adicionar"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

